import { Router } from '@angular/router';
import { WorkspaceNavigationService } from 'app/core/services/workspace/workspace-navigation.service';
import { AuthService } from 'shared-service/services/auth/auth.service';
import { TopupAlertPopupComponent } from './topup-alert-popup.component';
import { playTopupChime, claimTopupAttention } from './topup-chime';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { EMPTY, Subject, catchError, exhaustMap, take, takeUntil, timer } from 'rxjs';
import { SharedModule } from 'shared-ui/shared.module';
import { TopupOrderService } from 'app/core/services/api/billing-managment/topup-order.service';
import { PrivilegeService } from 'app/core/services/privilege/privilege.service';
import { eSystemFunction } from 'app/core/enums/shipment-management/enum-shipment-report.enum';
import { ePermission } from 'shared-domains/enums/permission.enum';
import { TopupAttention } from 'app/core/domains/billing-managment/history/topup-attention.model';
import { TopupOrder } from 'app/core/domains/billing-managment/history/topup-order.model';
import { NotificationRealtimeService } from 'app/core/services/realtime/notification-realtime.service';

@Component({
    selector: 'topup-attention', standalone: true, imports: [SharedModule, TopupAlertPopupComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './topup-attention.component.html', styleUrls: ['./topup-attention.component.scss'],
})
export class TopupAttentionComponent implements OnInit, OnDestroy {
    AlertVisible = false;
    private popupKey = "";
    private alive = true;
    private refreshListener = () => this.refresh$.next('');
    Model = new TopupAttention(); Visible = false; Loading = false; Failed = false; Navigating = false;
    CanReview = false; Sound = false; Pulse = false;
    private destroy$ = new Subject<void>();
    private refresh$ = new Subject<string>();
    private audio?: AudioContext;
    private pulseTimer?: ReturnType<typeof setTimeout>;
    private initialized = false;
    private latestId = 0;
    private visualKey = "";
    constructor(private api: TopupOrderService, private privileges: PrivilegeService,
        private realtime: NotificationRealtimeService, private cdr: ChangeDetectorRef,
        private router: Router, private workspace: WorkspaceNavigationService, private auth: AuthService) {}
    ngOnInit(): void {
        window.addEventListener('topup-policy-changed', this.refreshListener);
        document.addEventListener('visibilitychange', this.refreshListener);
        this.privileges.getFunctionsForAccount().pipe(take(1), takeUntil(this.destroy$)).subscribe(groups => {
            this.CanReview = groups.flatMap(g => g.SystemFunctions ?? []).some(f =>
                f.SystemFunctionId === eSystemFunction.SYSTEM_FUNCTION_WALLET_TOPUP &&
                f.PermissionIds?.includes(ePermission.PERMISSION_APPROVE));
            if (!this.CanReview) return;
            this.refresh$.pipe(exhaustMap(() => this.api.getTopupAttention(this.Visible ? 100 : 10).pipe(
                catchError(() => { this.Failed = true; this.Loading = false; this.cdr.markForCheck(); return EMPTY; }),
                // A refresh verifies that a notification still has actionable orders before sounding.
                // An initial page load establishes the baseline without replaying old alerts.
                )), takeUntil(this.destroy$)).subscribe(model => this.accept(model));
            timer(0, 30_000).pipe(takeUntil(this.destroy$)).subscribe(() => this.refresh$.next(''));
            this.realtime.notification$.pipe(takeUntil(this.destroy$)).subscribe(() => this.refresh$.next(''));
            this.cdr.markForCheck();
        });
    }
    private accept(model: TopupAttention): void {
        if (model.Scope !== this.Model.Scope) { this.initialized = false; this.latestId = 0; this.popupKey = ""; this.AlertVisible = false; }
        const newest = Math.max(0, ...model.Orders.map(o => o.TopupOrderId));
        const changed = this.initialized && newest > this.latestId;
        this.latestId = Math.max(this.latestId, newest); this.Model = model; this.Failed = false; this.Loading = false;
        try { this.Sound = localStorage.getItem('topup-sound:' + model.Scope) === 'on'; } catch { this.Sound = false; }
        const reminder = model.ReminderKey;
        if (this.initialized && model.Enabled && model.PendingCount > 0 && (changed || reminder.length > 0)) {
            void this.notifyOnce(changed ? 'new:' + newest : 'reminder:' + reminder);
        }
        if (model.Enabled && model.PendingCount > 0) {
            if (!this.initialized && model.PopupOnLogin && this.auth.notificationSessionKey)
                this.popupKey = 'login:' + this.auth.notificationSessionKey;
            if (changed && model.PopupOnNew) this.popupKey = 'new:' + newest;
            void this.showPopupOnce();
        } else { this.AlertVisible = false; this.popupKey = ''; }
        this.initialized = true;
        if (!model.PendingCount) { this.Pulse = false; clearTimeout(this.pulseTimer); }
        this.cdr.markForCheck();
    }
    private async showPopupOnce(): Promise<void> {
        if (!this.popupKey || this.Visible || document.visibilityState !== 'visible' || !navigator.locks) return;
        await navigator.locks.request('topup-popup:' + this.Model.Scope, async () => {
            if (!this.alive || !this.popupKey || !this.Model.Enabled || !this.Model.PendingCount || this.Visible
                || document.visibilityState !== 'visible' || (!this.AlertVisible && document.querySelector('.dx-popup-wrapper:not(.dx-state-invisible)'))) return;
            const key = this.popupKey;
            if ((key.startsWith('login:') && !this.Model.PopupOnLogin) || (key.startsWith('new:') && !this.Model.PopupOnNew)) { this.popupKey = ''; return; }
            try {
                if (!claimTopupAttention(localStorage, 'popup:' + this.Model.Scope, key)) { this.popupKey = ''; return; }
                this.popupKey = ''; this.AlertVisible = true; this.cdr.markForCheck();
            } catch { /* Storage unavailable: keep the badge and manual queue usable. */ }
        });
    }
    private async notifyOnce(key: string): Promise<void> {
        if (this.Model.IconEnabled && this.visualKey !== key) {
            this.visualKey = key;
            this.Pulse = true; clearTimeout(this.pulseTimer);
            this.pulseTimer = setTimeout(() => { this.Pulse = false; this.cdr.markForCheck(); }, 6000);
            this.cdr.markForCheck();
        }
        // Tabs that cannot play audio must not consume the shared sound claim.
        if (!navigator.locks || !this.Sound || !this.Model.SoundEnabled || this.audio?.state !== 'running') return;
        await navigator.locks.request('topup-audio:' + this.Model.Scope, async () => {
            if (!this.CanReview || !this.Model.Enabled || !this.Model.PendingCount) return;
            try { if (!claimTopupAttention(localStorage, this.Model.Scope, key)) return; } catch { return; }
            this.chime();
        });
    }
    async toggleSound(): Promise<void> {
        try {
            const wasReady = this.SoundReady;
            this.audio ??= new AudioContext(); await this.audio.resume();
            this.Sound = !wasReady;
            localStorage.setItem('topup-sound:' + this.Model.Scope, this.Sound ? 'on' : 'off');
            if (this.Sound) this.chime();
        } catch { this.Sound = false; }
        this.cdr.markForCheck();
    }
    get SoundReady(): boolean { return this.Sound && this.audio?.state === 'running'; }
    private chime(): void { if (this.audio) playTopupChime(this.audio, this.Model.RingCount); }
    open(): void { this.Visible = true; this.Loading = true; this.refresh$.next(''); }
    async review(order: TopupOrder): Promise<void> {
        if (!this.CanReview || this.Loading || this.Navigating) return;
        if (!Number.isSafeInteger(order.TopupOrderId) || order.TopupOrderId <= 0
            || !Number.isSafeInteger(order.WalletInfoId) || order.WalletInfoId <= 0) {
            this.Failed = true;
            this.cdr.markForCheck();
            return;
        }
        this.Navigating = true;
        this.Failed = false;
        try {
            // The wallet page owns review UI; the header only links to the selected request.
            const navigated = await this.router.navigate(['/systems/wallet'], {
                queryParams: this.workspace.queryParams({
                    walletInfoId: order.WalletInfoId,
                    topupOrderId: order.TopupOrderId,
                }),
            });
            this.Failed = !navigated;
            if (navigated) this.Visible = false;
        } catch {
            this.Failed = true;
        } finally {
            this.Navigating = false;
            this.cdr.markForCheck();
        }
    }
    ngOnDestroy(): void {
        this.alive = false;
        window.removeEventListener('topup-policy-changed', this.refreshListener);
        document.removeEventListener('visibilitychange', this.refreshListener);
        this.CanReview = false; this.destroy$.next(); this.destroy$.complete(); clearTimeout(this.pulseTimer);
        void this.audio?.close();
    }
}
